import React from 'react';
import { useLocation } from 'react-router-dom';
import SentryIntroduction from '../../pages/sentry-docs/SentryIntroduction';
import SentryQuickStart from '../../pages/sentry-docs/SentryQuickStart';
import GettingStartedIntroduction from '../../pages/sentry-docs/GettingStartedIntroduction';
import GroupSetupOverview from '../../pages/sentry-docs/GroupSetupOverview';
import GroupSetupInitial from '../../pages/sentry-docs/GroupSetupInitial';
import GroupSetupTraders from '../../pages/sentry-docs/GroupSetupTraders';
import GroupSetupWallets from '../../pages/sentry-docs/GroupSetupWallets';
import GroupSetupPayoutWallets from '../../pages/sentry-docs/GroupSetupPayoutWallets';
import GroupSetupSettings from '../../pages/sentry-docs/GroupSetupSettings';
import TradingOverview from '../../pages/sentry-docs/TradingOverview';
import TradingCallingTokens from '../../pages/sentry-docs/TradingCallingTokens';
import TradingDCA from '../../pages/sentry-docs/TradingDCA';
import TradingViewingPositions from '../../pages/sentry-docs/TradingViewingPositions';
import TradingSellingPositions from '../../pages/sentry-docs/TradingSellingPositions';
import TradingTakeProfit from '../../pages/sentry-docs/TradingTakeProfit';
import AnalyticsOverview from '../../pages/sentry-docs/AnalyticsOverview';
import AnalyticsBalance from '../../pages/sentry-docs/AnalyticsBalance';
import AnalyticsStats from '../../pages/sentry-docs/AnalyticsStats';
import AnalyticsLeaderboard from '../../pages/sentry-docs/AnalyticsLeaderboard';
import AnalyticsPnL from '../../pages/sentry-docs/AnalyticsPnL';
import DeploymentOverview from '../../pages/sentry-docs/DeploymentOverview';
import DeploymentDeployWizard from '../../pages/sentry-docs/DeploymentDeployWizard';
import DeploymentDeployerMenu from '../../pages/sentry-docs/DeploymentDeployerMenu';
import DeploymentBundleWallets from '../../pages/sentry-docs/DeploymentBundleWallets';
import DeploymentBundleSell from '../../pages/sentry-docs/DeploymentBundleSell';
import DeploymentBundleWithdrawHarvest from '../../pages/sentry-docs/DeploymentBundleWithdrawHarvest';
import MenuReferenceOverview from '../../pages/sentry-docs/MenuReferenceOverview';

const SentryDocsRouter: React.FC = () => {
  const location = useLocation();
  
  // Check if we're on sentry.trading domain
  const isSentryDomain = typeof window !== 'undefined' && 
    (window.location.hostname === 'sentry.trading' || 
     window.location.hostname === 'www.sentry.trading');
  
  // Normalize path - handle both /sentry/docs and /docs paths
  const getNormalizedPath = (path: string): string => {
    // If on sentry.trading domain and path starts with /docs, convert to /sentry/docs
    if (isSentryDomain && path.startsWith('/docs')) {
      return path.replace('/docs', '/sentry/docs');
    }
    return path;
  };
  
  const normalizedPath = getNormalizedPath(location.pathname);
  
  const renderPage = () => {
    // Handle root docs path
    if (normalizedPath === '/sentry/docs' || normalizedPath === '/docs') {
      return <SentryIntroduction />;
    }
    
    // Handle all other paths
    switch (normalizedPath) {
      case '/sentry/docs/quick-start':
        return <SentryQuickStart />;
      case '/sentry/docs/getting-started':
        return <GettingStartedIntroduction />;
      case '/sentry/docs/group-setup':
        return <GroupSetupOverview />;
      case '/sentry/docs/group-setup/initial-setup':
        return <GroupSetupInitial />;
      case '/sentry/docs/group-setup/traders':
        return <GroupSetupTraders />;
      case '/sentry/docs/group-setup/wallets':
        return <GroupSetupWallets />;
      case '/sentry/docs/group-setup/payout-wallets':
        return <GroupSetupPayoutWallets />;
      case '/sentry/docs/group-setup/settings':
        return <GroupSetupSettings />;
      case '/sentry/docs/trading':
        return <TradingOverview />;
      case '/sentry/docs/trading/calling-tokens':
        return <TradingCallingTokens />;
      case '/sentry/docs/trading/dca':
        return <TradingDCA />;
      case '/sentry/docs/trading/viewing-positions':
        return <TradingViewingPositions />;
      case '/sentry/docs/trading/selling-positions':
        return <TradingSellingPositions />;
      case '/sentry/docs/trading/take-profit':
        return <TradingTakeProfit />;
      case '/sentry/docs/analytics':
        return <AnalyticsOverview />;
      case '/sentry/docs/analytics/balance':
        return <AnalyticsBalance />;
      case '/sentry/docs/analytics/stats':
        return <AnalyticsStats />;
      case '/sentry/docs/analytics/leaderboard':
        return <AnalyticsLeaderboard />;
      case '/sentry/docs/analytics/pnl':
        return <AnalyticsPnL />;
      case '/sentry/docs/deployment':
        return <DeploymentOverview />;
      case '/sentry/docs/deployment/deploy-wizard':
        return <DeploymentDeployWizard />;
      case '/sentry/docs/deployment/deployer-menu':
        return <DeploymentDeployerMenu />;
      case '/sentry/docs/deployment/bundle-wallets':
        return <DeploymentBundleWallets />;
      case '/sentry/docs/deployment/bundle-sell':
        return <DeploymentBundleSell />;
      case '/sentry/docs/deployment/bundle-withdraw-harvest':
        return <DeploymentBundleWithdrawHarvest />;
      case '/sentry/docs/menu-reference':
        return <MenuReferenceOverview />;
      default:
        // Fallback to introduction
        return <SentryIntroduction />;
    }
  };

  return renderPage();
};

export default SentryDocsRouter;
